import {
  ESTIMATED_AT_FALLBACK_RATE,
  formatCents,
  type AiCostSummary,
  type AiModelConfig,
} from '@/lib/api/ai-costs';
import { DataGrid, type DataGridColumn } from '@/components/ui/data-grid';

/**
 * Model pinning + per-work-class breakdown (T-0112 / C20-122).
 *
 * Renders TWO related cards stacked vertically:
 *   1. Pinned-model summary — the reasoning (chat) deployment every work
 *      class defaults to and the embedding deployment, read from the
 *      server's configuration (MODEL_CHAT, EMBEDDING_MODEL) rather than a
 *      placeholder. A deployment with no rate-card row is labelled: its
 *      spend is priced at the fallback rate and is an estimate.
 *   2. Per-work-class cost table — calls / avg-tokens / $/call / MTD
 *      total / share-of-spend bar. Mirrors the prototype's `wcRows`
 *      block in `screens-soft-gaps.js`.
 */
export interface ModelPinningTableProps {
  summary: AiCostSummary;
  /** Null while loading or when the server predates the endpoint. */
  model: AiModelConfig | null;
}

interface WorkClassRow {
  id: string;
  name: string;
  label: string;
  tickets: string;
  calls: number;
  avg_tokens: number;
  cost_per_call_cents: number;
  total_cents: number;
  share_pct: number;
}

export function ModelPinningTable({ summary, model }: ModelPinningTableProps) {
  const totalCents = summary.per_work_class.reduce((acc, w) => acc + w.total_cents, 0);
  const rows: WorkClassRow[] = summary.per_work_class.map((w) => ({
    id: w.id,
    name: w.name,
    label: w.label,
    tickets: w.tickets,
    calls: w.calls,
    avg_tokens: w.avg_tokens,
    cost_per_call_cents: w.cost_per_call_cents,
    total_cents: w.total_cents,
    share_pct: totalCents > 0 ? (w.total_cents / totalCents) * 100 : 0,
  }));

  const columns: DataGridColumn<WorkClassRow>[] = [
    {
      key: 'name',
      header: 'Work class',
      cell: (r) => (
        <div>
          <div className="font-medium text-ink-900">{r.name}</div>
          <div className="text-meta text-ink-500">{r.label}</div>
        </div>
      ),
    },
    {
      key: 'calls',
      header: 'Calls',
      headerClassName: 'text-right',
      cellClassName: 'text-right font-mono',
      cell: (r) => r.calls.toLocaleString(),
    },
    {
      key: 'avg_tokens',
      header: 'Avg tokens',
      headerClassName: 'text-right',
      cellClassName: 'text-right font-mono',
      cell: (r) => `${(r.avg_tokens / 1000).toFixed(1)}k`,
    },
    {
      key: 'cost_per_call',
      header: '$/call',
      headerClassName: 'text-right',
      cellClassName: 'text-right font-mono',
      cell: (r) => {
        // The embedding row has cost-per-call far below 1 cent — surface
        // its dollar value with 4 decimals so the order-of-magnitude is
        // visible. The prototype uses the same "<$0.01 → 4-decimal" rule.
        if (r.cost_per_call_cents === 0 && r.id === 'embed') {
          // For the embed row use a fractional cents derivation from the
          // total + calls so the displayed value isn't $0.0000.
          if (r.calls > 0) {
            const usd = r.total_cents / r.calls / 100;
            return `$${usd.toFixed(4)}`;
          }
          return '$0.0000';
        }
        if (r.cost_per_call_cents < 1) {
          return `$${(r.cost_per_call_cents / 100).toFixed(4)}`;
        }
        return formatCents(r.cost_per_call_cents);
      },
    },
    {
      key: 'total',
      header: 'MTD total',
      headerClassName: 'text-right',
      cellClassName: 'text-right font-mono',
      cell: (r) => formatCents(r.total_cents),
    },
    {
      key: 'share',
      header: 'Share',
      cell: (r) => (
        <div className="flex items-center gap-2">
          <div className="relative h-1.5 flex-1 rounded-full bg-ink-100">
            <div
              className="absolute left-0 top-0 h-full rounded-full bg-ink-700"
              style={{ width: `${Math.min(100, r.share_pct)}%` }}
            />
          </div>
          <span className="min-w-[42px] text-right font-mono text-meta text-ink-700">
            {r.share_pct.toFixed(1)}%
          </span>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <section
        aria-labelledby="model-pinning-heading"
        className="rounded-card border border-ink-100 bg-paper-0 p-card-pad"
      >
        <h2 id="model-pinning-heading" className="text-h3 font-semibold text-ink-900">
          Model pinning
        </h2>
        <p className="text-meta text-ink-500">
          The deployments the server is configured with (MODEL_CHAT, EMBEDDING_MODEL). Every work
          class uses the reasoning model unless overridden.
        </p>

        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <ModelCell
            label="Reasoning model"
            name={model?.chat_model ?? null}
            priced={model?.chat_model_priced ?? true}
          />
          <ModelCell
            label="Embedding model"
            name={model?.embedding_model ?? null}
            priced={model?.embedding_model_priced ?? true}
          />
        </div>
      </section>

      {summary.estimated_models.length > 0 ? (
        <p className="text-meta text-ink-700">
          Spend from <span className="font-mono">{summary.estimated_models.join(', ')}</span> is{' '}
          {ESTIMATED_AT_FALLBACK_RATE} (no rate-card row for the model).
        </p>
      ) : null}

      <section
        aria-labelledby="work-class-heading"
        className="overflow-hidden rounded-card border border-ink-100 bg-paper-0"
      >
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-ink-100 px-card-pad py-3">
          <div>
            <h2 id="work-class-heading" className="text-h3 font-semibold text-ink-900">
              Work-class breakdown
            </h2>
            <p className="text-meta text-ink-500">
              Per-call econ · totals for MTD across all engagements
            </p>
          </div>
          <span className="font-mono text-meta text-ink-500">{formatCents(totalCents)} total</span>
        </div>
        <DataGrid columns={columns} rows={rows} rowKey={(r) => r.id} />
      </section>
    </div>
  );
}

function ModelCell({
  label,
  name,
  priced,
}: {
  label: string;
  name: string | null;
  priced: boolean;
}) {
  return (
    <div>
      <div className="text-meta uppercase tracking-wider text-ink-500">{label}</div>
      <div className="mt-1 font-mono text-body font-medium text-ink-900">
        {name ?? 'Not configured'}
      </div>
      <div className="text-meta text-ink-500">
        {name === null
          ? 'The server has no deployment set.'
          : priced
            ? 'Priced from the rate card.'
            : `Spend ${ESTIMATED_AT_FALLBACK_RATE}: no rate-card row for this model.`}
      </div>
    </div>
  );
}
